export const passwordMinLength = 12

const SUBSTR_MIN = 3

export type PolicyCtx = {
  username: string
  email: string
}

export enum PwRule {
  Length = 'LENGTH',
  Classes = 'CLASSES',
  NoRepeat = 'NO_REPEAT',
  NoSeq = 'NO_SEQ',
  NoHints = 'NO_HINTS',
}

export const pwRuleOrder: PwRule[] = [
  PwRule.Length,
  PwRule.Classes,
  PwRule.NoRepeat,
  PwRule.NoSeq,
  PwRule.NoHints,
]

export const pwRuleLabel: Record<PwRule, string> = {
  [PwRule.Length]: 'At least 12 characters',
  [PwRule.Classes]: 'Includes uppercase, lowercase, a number, and a symbol',
  [PwRule.NoRepeat]: 'No four or more repeated characters in a row',
  [PwRule.NoSeq]: 'No obvious sequences (like 1234 or abcd)',
  [PwRule.NoHints]: 'Cannot contain your username or email address',
}

const hasClass = (pw: string, re: RegExp): boolean => re.test(pw)

const hasRepeatRun = (pw: string): boolean => /(.)\1{3,}/.test(pw)

const isSeqWindow = (w: string): boolean => {
  if (w.length !== 4) return false
  const lower = w.toLowerCase()
  const c0 = lower.charCodeAt(0)
  const c1 = lower.charCodeAt(1)
  const c2 = lower.charCodeAt(2)
  const c3 = lower.charCodeAt(3)
  const asc = (x0: number, x1: number, x2: number, x3: number): boolean =>
    x1 === x0 + 1 && x2 === x1 + 1 && x3 === x2 + 1
  const desc = (x0: number, x1: number, x2: number, x3: number): boolean =>
    x1 === x0 - 1 && x2 === x1 - 1 && x3 === x2 - 1
  if (/^\d{4}$/.test(lower)) {
    return asc(c0, c1, c2, c3) || desc(c0, c1, c2, c3)
  }
  if (/^[a-z]{4}$/.test(lower)) {
    return asc(c0, c1, c2, c3) || desc(c0, c1, c2, c3)
  }
  return false
}

const hasSeqRun = (pw: string): boolean => {
  for (let i = 0; i <= pw.length - 4; i++) {
    if (isSeqWindow(pw.slice(i, i + 4))) return true
  }
  return false
}

const hasUserHints = (pwLower: string, ctx: PolicyCtx): boolean => {
  const u = ctx.username?.trim().toLowerCase() ?? ''
  if (u.length >= SUBSTR_MIN && pwLower.includes(u)) return true
  const e = ctx.email?.trim().toLowerCase() ?? ''
  if (e.length >= SUBSTR_MIN && pwLower.includes(e)) return true
  const local = e.includes('@') ? (e.split('@')[0] ?? '') : ''
  if (local.length >= SUBSTR_MIN && pwLower.includes(local)) return true
  return false
}

export const rulePass = (
  password: string,
  ctx: PolicyCtx,
  rule: PwRule,
): boolean => {
  if (typeof password !== 'string') return false
  const lower = password.toLowerCase()
  switch (rule) {
    case PwRule.Length:
      return password.length >= passwordMinLength
    case PwRule.Classes:
      return (
        hasClass(password, /[A-Z]/) &&
        hasClass(password, /[a-z]/) &&
        hasClass(password, /\d/) &&
        hasClass(password, /[^A-Za-z0-9]/)
      )
    case PwRule.NoRepeat:
      return !hasRepeatRun(password)
    case PwRule.NoSeq:
      return !hasSeqRun(password)
    case PwRule.NoHints:
      return !hasUserHints(lower, ctx)
    default:
      return false
  }
}

export const policyFail = (password: string, ctx: PolicyCtx): string | null => {
  if (typeof password !== 'string') return 'Password is required.'
  if (!rulePass(password, ctx, PwRule.Length)) {
    return `Password must be at least ${passwordMinLength} characters.`
  }
  if (!rulePass(password, ctx, PwRule.Classes)) {
    return 'Password must include uppercase, lowercase, a number, and a symbol.'
  }
  if (!rulePass(password, ctx, PwRule.NoRepeat)) {
    return 'Password cannot contain four or more repeated characters in a row.'
  }
  if (!rulePass(password, ctx, PwRule.NoSeq)) {
    return 'Password cannot contain obvious sequences (such as 1234 or abcd).'
  }
  if (!rulePass(password, ctx, PwRule.NoHints)) {
    return 'Password cannot contain your username or email address.'
  }
  return null
}
